/* Draft: renders store policy locally; never writes descriptions or checkout rates. */
(() => {
  const clean = value => String(value ?? '').replace(/\s+/g, ' ').trim();
  const frp = value => /\bfrp\b|\bfib(?:re|er)glass\b|\bfib(?:re|er) glass\b/i.test(value);
  const shippingHeading = value => /^(?:shipping(?: time)?|delivery(?: time)?(?: free)?|courier delivery|delvery)\s*:?$/i.test(clean(value));
  const shippingLine = value => /^(?:(?:free|default|recommended)\s+)?(?:world\s*wide (?:shipping|delivery)|delvery|delivery(?: method)?|shipping(?: time)?|sea freight|air freight|courier)\b/i.test(clean(value));
  const list = lines => {
    const ul = document.createElement('ul');
    for (const line of lines) { const li = document.createElement('li'); li.textContent = line; ul.append(li); }
    return ul;
  };
  const group = (title, nodes) => ({title, nodes, contents: false, optional: false, key: null});
  const marker = name => { const node = document.createElement('div'); node.dataset[name] = ''; return node; };

  function prepare(groups, source, data) {
    // Match the original component's refusal to process executable/interactive markup.
    if (!groups || source.querySelector('script,style,form')) return groups;
    data.ruleSourceText = source.textContent;
    const result = [], wheels = [];
    for (const original of groups) {
      const g = {...original, nodes: original.nodes.map(n => n.cloneNode(true))};
      if (shippingHeading(g.title)) {
        // Preserve non-delivery notes such as trademark disclaimers and fitting warnings.
        const notes = [];
        for (const node of g.nodes) {
          const candidates = node.nodeType === 1 && /^(UL|OL)$/.test(node.tagName) ? [...node.children] : [node];
          for (const part of candidates) {
            if (!clean(part.textContent)) continue;
            if (!/(freight|delivery|shipping|transit|door.to.door|\bddp\b|\bddu\b|dut(?:y|ies)|customs|\d+\s*(?:[–-]\s*\d+)?\s*(?:days?|months?))/i.test(part.textContent)) {
              const p = document.createElement('p'); p.textContent = clean(part.textContent); notes.push(p);
            }
          }
        }
        if (notes.length) result.push(group('Additional information', notes));
        continue;
      }
      const holder = document.createElement('div'); holder.append(...g.nodes);
      for (const node of [...holder.querySelectorAll('p,li')]) {
        const t = clean(node.textContent);
        if (/^Wheel Specs used on/i.test(t) && !/wheel/i.test(g.title)) { wheels.push(node.cloneNode(true)); node.remove(); continue; }
        // Shipping inside contents must not retain a stale free-delivery claim.
        if (shippingLine(t) && !node.querySelector('img,table,iframe')) {
          const replacement = document.createElement(node.tagName === 'LI' ? 'li' : 'p');
          replacement.dataset.rpDeliverySummary = ''; replacement.textContent = 'See delivery details below.';
          node.replaceWith(replacement);
        }
        // Only the specific filler reviewed by the merchant is removed automatically.
        if (/^Enhance your Aston Martin DB11 with our Dry Carbon Fiber Front Lip \/ Splitter$/i.test(t)) node.remove();
        if (t.includes("It's the ideal upgrade or replacement, offering premium quality without dealer prices.")) node.textContent = t.replace("It's the ideal upgrade or replacement, offering premium quality without dealer prices.", '').trim();
      }
      g.nodes = [...holder.childNodes];
      if (g.nodes.some(n => clean(n.textContent) || n.nodeType === 1 && n.querySelector('img,table,iframe'))) result.push(g);
    }
    if (wheels.length) {
      const ul = document.createElement('ul');
      for (const n of wheels) { const li = document.createElement('li'); li.textContent = clean(n.textContent); ul.append(li); }
      result.push(group('Recommended Wheel Specification', [ul]));
    }
    const explicit = Array.isArray(data.fitmentLines) ? data.fitmentLines.filter(x => clean(x)) : [];
    const linked = (Array.isArray(data.linkedModels) ? data.linkedModels : []).filter(x => clean(x)).map(model => {
      const make = clean(data.carMake); return make && !clean(model).toLowerCase().startsWith(make.toLowerCase()) ? `${make} ${model}` : model;
    });
    const fitment = explicit.length ? explicit : linked;
    if (fitment.length) {
      const existing = result.find(g => /fits the following|compatib|^fits:?$/i.test(g.title));
      if (existing) {
        // Existing restrictions remain; structured information is supplemental.
        const old = existing.nodes.map(n => clean(n.textContent)).join(' ').toLowerCase();
        const additions = fitment.filter(line => !old.includes(clean(line).toLowerCase()));
        if (additions.length) existing.nodes.push(list(additions));
      } else result.push(group('Fits the Following', [list(fitment)]));
    }
    data.ruleHasFrp = data.containsFrp === true || frp([data.productTitle, data.ruleSourceText, ...(data.variants || []).map(v => v.title)].join(' '));
    if (data.ruleHasFrp || (data.variants || []).some(v => v.containsFrp === true)) {
      result.push(group('FRP preparation & dry-fitting', [marker('rpFrpGuidance')]));
    }
    result.push(group('Delivery', [marker('rpDeliveryRules')]));
    return result;
  }

  function modeFor(data, variant) {
    const explicit = clean(variant?.deliveryMethod || data.deliveryMethod).toLowerCase();
    if (['sea', 'sea freight'].includes(explicit)) return 'sea';
    if (['courier', 'air', 'air freight'].includes(explicit)) return 'courier';
    // Only a positive explicit statement counts. "No courier" must not select courier.
    const statements = String(data.ruleSourceText || '').split(/[.\n;]/);
    return statements.some(s => /\bair freight\b|\bcourier\b/i.test(s) && !/\b(?:no|not|unavailable|cannot)\b/i.test(s)) ? 'courier' : 'sea';
  }

  function render(component, variant) {
    const data = component.data, mode = modeFor(data, variant);
    const known = variant && typeof variant.weightGrams === 'number' && Number.isFinite(variant.weightGrams);
    const free = known && variant.weightGrams === 20000;
    const charge = !variant ? 'Select a product option to see delivery charges.' : !known ? 'Delivery charges are confirmed at checkout.' : free ? (mode === 'sea' ? 'Free sea freight shipping.' : 'Free courier delivery.') : (mode === 'sea' ? 'Sea freight shipping is charged at checkout.' : 'Courier delivery is charged at checkout.');
    const lines = mode === 'sea' ? [charge,
      'Estimated transit time: 30–50 days, depending on the destination country.',
      'Door-to-door delivery, supplied Delivered Duty Paid (DDP) for most destinations.',
      'Delivered Duty Unpaid (DDU) applies to some countries.',
      'We will always try to arrange Delivered Duty Paid (DDP) delivery where possible.'
    ] : [charge,
      'Estimated transit time: 5–10 days, depending on the destination country.',
      'Door-to-door delivery. We will always try to arrange Delivered Duty Paid (DDP) delivery where possible.',
      'Where DDP is unavailable, delivery is supplied Delivered Duty Unpaid (DDU). Please expect to pay customs duty on import.',
      'Where DDP delivery is available, estimated transit time is 10–15 days, depending on the destination country.'
    ];
    for (const target of component.querySelectorAll('[data-rp-delivery-rules]')) target.replaceChildren(list(lines));
    for (const target of component.querySelectorAll('[data-rp-delivery-summary]')) {
      target.textContent = charge; target.dataset.shipping = mode === 'sea' ? 'sea' : 'air';
    }
    for (const target of component.querySelectorAll('[data-rp-frp-guidance]')) {
      const show = variant?.containsFrp !== false && (data.ruleHasFrp || variant?.containsFrp === true);
      const details = target.closest('details'); if (details) details.hidden = !show;
      if (show) target.replaceChildren(list([
        'For FRP parts: unpainted parts require preparation and painting before final installation.',
        'Dry-fit all FRP parts before painting. Minor trimming or adjustment may be required to achieve the correct fit.'
      ]));
    }
  }
  window.RobotProductRules = {prepare, render, modeFor};
})();
